import React from "react";
import { NextPage } from "next";
import Link from "next/link";
import { useRouter } from "next/router";
import Head from "next/head";
import { useTransition } from "@vanyapr/transition-hook";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// TODO(gj): Pick an icon family and stick to it
import {
  HiLink,
  HiOutlineArrowRight,
  HiOutlineCheck,
  HiOutlineChevronRight,
  HiOutlineClipboardCopy,
  HiOutlinePlus,
  HiOutlineX,
  HiXCircle,
} from "react-icons/hi";

import Button from "@/components/Button";
import {
  decodeState,
  encodeState,
  State,
  CURRENT_STATE_VERSION,
  EXAMPLES,
  StateWithMetadata,
  updateResourceIsGrantedAccessTo,
  grantedParents,
  updateRoleInheritanceConfiguration,
  editRole,
  addRole,
  deleteRole,
  editPermission,
  deletePermission,
  toggleRolePermissionImplication,
  addPermission,
  toggleCanShareWithOtherUsers,
  toggleCanShareWithParent,
  toggleCanSharePublic,
  toggleCanShareWithOtherUsersRole,
  updateCanShareWithParentInheritance,
  toggleCanSharePublicPermission,
} from "@/src/modeler/state";
import { useSegment } from "@/src/segment";
import ModelerQuiz from "@/components/modeler/ModelerQuiz";
import AnimatedPanel from "@/components/modeler/AnimatedPanel";
import ResourceHierarchy from "@/components/modeler/ResourceHierarchy";
import Modal from "@/components/Modal";
import {
  EMPTY_TEST_BLOCK,
  TestBlock,
  roleInheritanceTest,
  sharePublicTest,
  shareWithOtherUsersTest,
  shareWithParentTest,
  stateToPolar,
} from "@/src/modeler/polar";
import { CopyableCodeBlockPolar } from "@/components/CopyableCodeBlock";
import { HiMiniArrowRight } from "react-icons/hi2";
import {
  AttributePrimitive,
  RelationPrimitive,
  RolePrimitive,
} from "@/components/modeler/Primitives";
import PrimitiveHint from "@/components/modeler/PrimitiveHint";
import ConcreteInteractiveExample, {
  EMPTY_TEST_STATUS,
  TestStatus,
} from "@/components/modeler/ConcreteInteractiveExample";
import TestVisualization from "@/components/modeler/TestVisualization";

type SegmentMetadata = {
  userId: string | null;
  groupId: string | null;
} | null;

/**
 * Unlike most of the app, THIS PAGE IS UNAUTHENTICATED!!!
 */
const ModelerPage: NextPage = () => {
  // ephemeral state
  const segment = useSegment();
  const [segmentMetadata, setSegmentMetadata] = useState<SegmentMetadata>(null);
  const [modalState, setModalState] = useState<ModalState>(null);
  const track = (event: string, properties: { [key: string]: unknown }) => {
    if (!segment) {
      console.log("SEGMENT DEV", event, properties);
      return;
    }
    segment
      .track(event, properties, {
        context: { app: { name: "ui" }, groupId: segmentMetadata?.groupId },
      })
      .catch((e) => console.error("Segment error:", e));
  };

  // Fetch user details from Segment if available.
  useEffect(() => {
    if (!segment) return;
    const user = segment.user();
    const userId = user.id() || user.anonymousId() || null;
    const group = segment.group();
    const groupId = group.id() || group.anonymousId() || null;
    setSegmentMetadata({ userId, groupId });
  }, [segment]);

  const router = useRouter();

  const updateUrl = (state: StateWithMetadata): void => {
    router.push(
      {
        query: {
          ...router.query,
          version: state.stateVersion,
          state: encodeState(state),
        },
      },
      undefined,
      { scroll: false, shallow: true }
    );
  };

  // putting this in a useMemo so we don't re-do this on every re-render
  const stateWithMetadata: StateWithMetadata | null = useMemo(() => {
    if (router.query.state) {
      return decodeState(router.query.state as string);
    } else {
      return null;
    }
  }, [router.query, segmentMetadata]);

  // Add current user to sharedBy state on initial page load.
  useEffect(() => {
    if (stateWithMetadata && segmentMetadata?.userId) {
      const { userId } = segmentMetadata;
      const sharedBy = [...(stateWithMetadata.sharedBy || []).filter(Boolean)];
      const currentUserIsLatestSharer =
        sharedBy.length > 0 && sharedBy[sharedBy.length - 1] === userId;
      if (!currentUserIsLatestSharer) sharedBy.push(userId);
      updateUrl({ ...stateWithMetadata, sharedBy });
    }
  }, [segmentMetadata?.userId, !!stateWithMetadata]);

  const setState = (state: State) =>
    updateUrl({
      ...(stateWithMetadata ?? {
        sharedBy: [],
        stateVersion: CURRENT_STATE_VERSION,
      }),
      state,
    });

  // Wait for router to be ready before rendering to avoid flashing `state ===
  // null` content when loading state from URL.
  if (!router.isReady) return null;

  const saveLink = typeof window !== "undefined" ? window.location.href : "";

  return (
    <>
      <Head>
        <title>Oso Modeler</title>
      </Head>
      <div className="flex flex-col h-screen overflow-hidden text-foreground">
        {/* Header */}
        <nav className="border-b border-border bg-card">
          <div className="px-4 lg:px-6">
            <div className="flex h-16 items-center space-x-2">
              <div className="flex flex-shrink-0 items-center space-x-2">
                <Link href="https://www.osohq.com" passHref={true}>
                  <img
                    alt="Oso Logo"
                    src="/logo-bear-black.png"
                    className="w-[50px] h-[25px] -mt-[5px] dark:invert"
                  />
                </Link>
                <div className="font-space text-xl">
                  <Link href="/modeler" passHref={true}>
                    <span>Oso</span> <span className="font-light">Modeler</span>
                  </Link>
                </div>
              </div>
              <div className="grow" />
              <Button
                className="border-border hover:border-border"
                disabled={!stateWithMetadata?.state.resources.length}
                medium
                onClick={() => {
                  setModalState("code");
                  track("Modeler - Preview Code - Modal Open", {});
                }}
                theme="secondary"
              >
                Preview Code
              </Button>
              <Button
                disabled={!stateWithMetadata?.state.resources.length}
                icon={<HiLink />}
                medium
                onClick={() => {
                  setModalState("save");
                  track("Modeler - Save - Modal Open", {});
                }}
                theme="primary"
              >
                Save
              </Button>
            </div>
          </div>
        </nav>

        <div className="grow">
          <AnimatedPanel right={!!stateWithMetadata}>
            <InitializeState track={track} setState={setState} />
            {stateWithMetadata && (
              <HierarchyEditor
                state={stateWithMetadata.state}
                setState={setState}
                track={track}
              />
            )}
          </AnimatedPanel>
        </div>
        <Modal
          isOpen={modalState === "code"}
          onClose={() => {
            setModalState(null);
            track("Modeler - Preview Code - Modal Close", {});
          }}
          showCloseButton
          wide
          blue
        >
          <div className="text-2xl font-bold">Preview of Model as Code</div>
          <Button
            block
            theme="primary"
            onClick={(e) => {
              e.preventDefault();
              if (!stateWithMetadata) return;
              track(
                "Modeler - Export to Oso Cloud - Try this in Oso Cloud button clicked",
                {}
              );
              const policy = stateToPolar(stateWithMetadata.state);
              const localStorageKey = "oso-modeler-import";
              try {
                localStorage.setItem(localStorageKey, policy);
                window.open("/");
              } catch (e) {
                console.error("failed to update localStorage:", e);
              }
            }}
          >
            Try this in Oso Cloud
          </Button>
          <CopyableCodeBlockPolar
            code={
              stateWithMetadata ? stateToPolar(stateWithMetadata.state) : ""
            }
          />
        </Modal>
        <Modal
          isOpen={modalState === "save"}
          onClose={() => {
            setModalState(null);
            track("Modeler - Save - Modal Close", {});
          }}
          showCloseButton
          blue
        >
          <div className="text-2xl font-bold">
            Save Your Authorization Model
          </div>
          <div className="mt-4">
            This is a permalink to your authorization model. Feel free to share
            it!
          </div>
          <div className="mt-4 flex space-x-2">
            <input
              type="text"
              className="bg-background text-foreground grow rounded"
              readOnly
              value={saveLink}
              onClick={(e) => {
                e.currentTarget.select();
                track("Modeler - Save - Link Clicked", {});
              }}
            />
            <CopyButton
              text={saveLink}
              onCopy={() => track("Modeler - Save - Copy Button Clicked", {})}
            />
          </div>
        </Modal>
      </div>
    </>
  );
};

const InitializeState: React.FC<{
  track: (event: string, properties: { [key: string]: unknown }) => void;
  setState: (state: State) => void;
}> = ({ track, setState }) => {
  const [doingQuiz, setDoingQuiz] = useState(false);
  const onClickExample = () => {
    // TODO(gj): 'Select an Example' interstitial page/modal once we have >1
    // examples to choose from.
    const example = EXAMPLES[0];
    track("Modeler - Start from Example", {
      example: example.name,
    });
    setState({ resources: example.resources });
  };

  return (
    <AnimatedPanel right={doingQuiz}>
      <div className="flex flex-col items-center p-4 font-light">
        <div className="flex flex-col justify-start w-full">
          <div className="text-xl mb-24">
            Model your application{"'"}s authorization.
          </div>
        </div>
        <div className="flex gap-4">
          <Button
            onClick={(e) => {
              e.preventDefault();
              onClickExample();
            }}
            theme="secondary"
          >
            <div className="flex justify-center items-center gap-1">
              <div>See an example model</div>
              <HiMiniArrowRight />
            </div>
          </Button>
          <Button
            onClick={(e) => {
              e.preventDefault();
              track("Modeler - Quiz - Start", {});
              setDoingQuiz(true);
            }}
            theme="primary"
          >
            <div className="flex justify-center items-center gap-1">
              <div>Start modeling</div>
              <HiMiniArrowRight />
            </div>
          </Button>
        </div>
      </div>
      <ModelerQuiz
        goBackToExamples={() => setDoingQuiz(false)}
        setState={setState}
        track={track}
      />
    </AnimatedPanel>
  );
};

type ModalState = "save" | "code" | null;

// Shamelessly copied from TokenManagementView
const CopyButton: React.FC<
  React.JSX.IntrinsicElements["button"] & {
    onCopy?: () => void;
    text: string;
  }
> = ({ onCopy, text }) => {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(() => {
    navigator.clipboard.writeText(text);
    setCopied(true);
  }, [text]);

  useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => setCopied(false), 5000);
      return () => clearTimeout(timeout);
    }
  }, [copied]);

  return (
    <Button
      medium
      icon={copied ? <HiOutlineCheck /> : <HiOutlineClipboardCopy />}
      onClick={() => {
        copy();
        if (onCopy) onCopy();
      }}
      theme="primary"
    >
      {copied ? "Copied" : "Copy"}
    </Button>
  );
};

const HierarchyEditor: React.FC<{
  state: State;
  setState: (state: State) => void;
  track: (event: string, properties: { [key: string]: unknown }) => void;
}> = ({ state, setState, track }) => {
  const [resource, setResource] = useState<string>("app");
  const [configuringResource, setConfiguringResource] =
    useState<boolean>(false);
  const { shouldMount: shouldMountResourceEditor, stage: resourceEditorStage } =
    useTransition(configuringResource, 150);
  const closeResourceEditor = useCallback(() => {
    setConfiguringResource(false);
    track("Modeler - Resource - Close", {});
  }, [setConfiguringResource]);
  const [test, setTest] = useState<TestBlock>(EMPTY_TEST_BLOCK);
  const [isTesting, setIsTesting] = useState(false);
  const testVisualizationContainerRef = useRef<HTMLDivElement>(null);
  const [{ width, height }, setTestVisualizationWidthAndHeight] = useState({
    width: 0,
    height: 0,
  });
  const [testStatus, setTestStatus] = useState<TestStatus>(EMPTY_TEST_STATUS);

  useEffect(() => {
    if (!testVisualizationContainerRef.current) return;
    const { width, height } =
      testVisualizationContainerRef.current.getBoundingClientRect();
    setTestVisualizationWidthAndHeight({ width, height });
  }, [testVisualizationContainerRef, resourceEditorStage, isTesting]);

  return (
    <div className="p-8 relative flex justify-between h-[calc(100vh-6rem)]">
      <div
        className={`transition-all duration-300 ${
          resourceEditorStage === "enter" ? "w-1/3" : "w-full"
        } ${isTesting ? "opacity-0" : "opacity-100"}`}
      >
        <ResourceHierarchy
          state={state}
          onClick={(resource) => {
            setResource(resource);
            setConfiguringResource(true);
            track("Modeler - Clicked on ERD node", { resource });
          }}
          animateOnChange={[resourceEditorStage === "enter"]}
          highlightedResource={
            resourceEditorStage === "enter" ? resource : undefined
          }
        />
      </div>
      {shouldMountResourceEditor && (
        <>
          <div
            className={`absolute inset-y-8 border border-border bg-card z-10 rounded-xl shadow-xl w-[calc(67%-2rem)] overflow-auto transition-all duration-300 ${
              resourceEditorStage === "enter"
                ? "opacity-1 translate-x-0 scale-100"
                : "opacity-0 translate-x-1/2 scale-95"
            } ${isTesting ? "right-1/3" : "right-4 lg:right-6"}`}
          >
            {!isTesting && (
              <button
                className="absolute top-2 right-2 text-xl transition-colors text-muted-foreground hover:text-error"
                onClick={closeResourceEditor}
              >
                <HiXCircle />
              </button>
            )}
            <ResourceEditor
              state={state}
              setState={setState}
              test={test}
              setTest={setTest}
              testStatus={testStatus}
              setTestStatus={setTestStatus}
              isTesting={isTesting}
              setIsTesting={setIsTesting}
              resourceName={resource}
              track={track}
            />
          </div>
          <div
            className={`transition-all flex justify-center -mr-2 ${
              resourceEditorStage === "enter" ? "w-1/3" : "w-0"
            } ${isTesting ? "opacity-100" : "opacity-0"}`}
            ref={testVisualizationContainerRef}
          >
            <TestVisualization
              test={test}
              testStatus={testStatus}
              width={width}
              height={height}
            />
          </div>
        </>
      )}
    </div>
  );
};

function renderListWithOr(list: string[]): React.JSX.Element {
  switch (list.length) {
    case 0:
      return <>(none)</>;
    case 1:
      return <span className="font-semibold">{list[0]}</span>;
    case 2:
      return (
        <>
          <span className="font-semibold">{list[0]}</span> or{" "}
          <span className="font-semibold">{list[1]}</span>
        </>
      );
    default:
      return (
        <>
          {list.slice(0, list.length - 1).map((item, i) => (
            <Fragment key={i}>
              <span key={i} className="font-semibold">
                {item}
              </span>
              {", "}
            </Fragment>
          ))}
          or <span className="font-semibold">{list[list.length - 1]}</span>
        </>
      );
  }
}

const ExampleButton: React.FC<{
  test: TestBlock | null;
  setTest: (test: TestBlock) => void;
  setIsTesting: (isTesting: boolean) => void;
  track: (event: string, properties: { [key: string]: unknown }) => void;
}> = ({ test, track, setTest, setIsTesting }) => {
  if (!test) return null;
  return (
    <Button
      xsmall
      onClick={() => {
        track("Modeler - Example - Open", { test: test.name });
        setTest(test);
        setIsTesting(true);
      }}
      className="ml-4"
    >
      Example
      <HiOutlineArrowRight className="ml-0.5" />
    </Button>
  );
};

const ResourceEditor: React.FC<{
  state: State;
  setState: (state: State) => void;
  test: TestBlock;
  setTest: (test: TestBlock) => void;
  testStatus: TestStatus;
  setTestStatus: (testStatus: TestStatus) => void;
  isTesting: boolean;
  setIsTesting: (isTesting: boolean) => void;
  resourceName: string;
  track: (event: string, properties: { [key: string]: unknown }) => void;
}> = ({
  state,
  setState,
  test,
  setTest,
  testStatus,
  setTestStatus,
  isTesting,
  setIsTesting,
  resourceName,
  track,
}) => {
  const [configuringAccess, setConfiguringAccess] = useState(false);
  const [configuringPermissions, setConfiguringPermissions] = useState(false);
  const resource = state.resources.find((r) => r.name === resourceName);
  if (!resource) return <div>oopsie!</div>;
  const boldResourceName = (
    <span className="font-semibold">{resourceName}</span>
  );
  return (
    <div className="p-4">
      <AnimatedPanel right={!isTesting}>
        <ConcreteInteractiveExample
          state={state}
          test={test}
          setTest={setTest}
          testStatus={testStatus}
          setTestStatus={setTestStatus}
          track={track}
          goBack={() => {
            track("Modeler - Example - Close", { test: test.name });
            setIsTesting(false);
          }}
        />
        <div>
          <div className="text-2xl font-bold">{resourceName}</div>
          <div className="flex justify-between items-center my-4 max-w-3xl">
            <div>
              <div className="text-lg">
                Users gain access to a particular {boldResourceName} if:
              </div>
              <ul className="list-disc pl-4">
                {resource.isGrantedAccessTo && (
                  <li>
                    they have a <RolePrimitive /> on the {boldResourceName}.
                    <ExampleButton
                      test={roleInheritanceTest(state, resource)}
                      setTest={setTest}
                      setIsTesting={setIsTesting}
                      track={track}
                    />
                  </li>
                )}
                {resource.relations.map((relation) => (
                  <li key={relation.resource}>
                    {/* TODO(gj): test 'its parent' vs. 'an associated' language here */}
                    they have access to its parent{" "}
                    <span className="font-semibold">{relation.resource}</span>.
                    <ExampleButton
                      test={roleInheritanceTest(state, resource)}
                      setTest={setTest}
                      setIsTesting={setIsTesting}
                      track={track}
                    />
                  </li>
                ))}
                {resource.sharing.canShareWithOtherUsers && (
                  <li>
                    it{"'"}s shared directly with them.
                    <ExampleButton
                      track={track}
                      setTest={setTest}
                      setIsTesting={setIsTesting}
                      test={shareWithOtherUsersTest(resource)}
                    />
                  </li>
                )}
                {resource.sharing.canShareWithParent.map(
                  (sharingWithParent) => (
                    <li key={sharingWithParent.parent}>
                      it{"'"}s shared with an associated{" "}
                      <span className="font-semibold">
                        {sharingWithParent.parent}
                      </span>{" "}
                      they have access to.
                      <ExampleButton
                        track={track}
                        setTest={setTest}
                        setIsTesting={setIsTesting}
                        test={shareWithParentTest(resource)}
                      />
                    </li>
                  )
                )}
                {resource.sharing.canSharePublic && (
                  <li>
                    it is shared publicly.
                    <ExampleButton
                      track={track}
                      setTest={setTest}
                      setIsTesting={setIsTesting}
                      test={sharePublicTest(resource)}
                    />
                  </li>
                )}
              </ul>
            </div>
            <button
              className={`rounded-full border transition-all p-2 bg-card hover:bg-muted ${
                configuringAccess ? "rotate-90" : "rotate-0"
              }`}
              onClick={() => {
                setConfiguringAccess(!configuringAccess);
                track("Modeler - Resource - Configure Access", {
                  open: !configuringAccess,
                });
              }}
            >
              <HiOutlineChevronRight />
            </button>
          </div>
          <div
            className={`border-l pl-4 pr-2 my-4 overflow-y-hidden transition-all ${
              configuringAccess ? "max-h-screen" : "max-h-0"
            }`}
          >
            <ResourceAccessEditor
              state={state}
              setState={setState}
              resourceName={resourceName}
              track={track}
            />
          </div>
          <div className="flex justify-between items-center my-4 max-w-3xl">
            <div className="text-lg">
              Users can {renderListWithOr(resource.permissions)} a particular{" "}
              {boldResourceName}.
            </div>
            <button
              className={`rounded-full border transition-all p-2 bg-card hover:bg-muted ${
                configuringPermissions ? "rotate-90" : "rotate-0"
              }`}
              onClick={() => {
                setConfiguringPermissions(!configuringPermissions);
                track("Modeler - Resource - Configure Permissions", {
                  open: !configuringPermissions,
                });
              }}
            >
              <HiOutlineChevronRight />
            </button>
          </div>
          <div
            className={`border-l pl-4 pr-2 my-4 overflow-y-hidden transition-all ${
              configuringPermissions ? "max-h-screen" : "max-h-0"
            }`}
          >
            <ResourcePermissionsEditor
              state={state}
              setState={setState}
              resourceName={resourceName}
              track={track}
            />
          </div>
        </div>
      </AnimatedPanel>
    </div>
  );
};

const EditableItem: React.FC<{
  value: string;
  setValue: (value: string) => void;
  deleteValue: () => void;
}> = ({ value, setValue, deleteValue }) => {
  const [currentValue, setCurrentValue] = useState(value);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setValue(currentValue);
      }}
      className="inline-block"
    >
      <input
        type="text"
        className="bg-background text-foreground py-0 rounded border-border mr-1 w-32"
        onChange={(e) => setCurrentValue(e.target.value.trim())}
        value={currentValue}
        onBlur={() => setValue(currentValue)}
      />
      <Button
        small
        theme="delete"
        onClick={(e) => {
          e.preventDefault();
          deleteValue();
        }}
        type="button"
      >
        <HiOutlineX />
      </Button>
    </form>
  );
};

const AddItem: React.FC<{
  addItem: (value: string) => void;
  placeholder: string;
}> = ({ addItem, placeholder }) => {
  const [currentValue, setCurrentValue] = useState("");
  const isEmpty = currentValue.trim() === "";
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (isEmpty) return;
        addItem(currentValue.trim());
        setCurrentValue("");
      }}
      className="inline-block"
    >
      <input
        type="text"
        className="bg-background text-foreground py-0 rounded border-border mr-1 w-32"
        onChange={(e) => setCurrentValue(e.target.value.trimStart())}
        value={currentValue}
        placeholder={placeholder}
      />
      <Button small type="submit" disabled={isEmpty}>
        <HiOutlinePlus />
      </Button>
    </form>
  );
};

const SharingEditor: React.FC<{
  state: State;
  setState: (state: State) => void;
  resourceName: string;
  track: (event: string, properties: { [key: string]: unknown }) => void;
}> = ({ state, setState, resourceName, track }) => {
  const resource = state.resources.find((r) => r.name === resourceName);
  if (!resource) return <div>oopsie!</div>;
  const boldResourceName = (
    <span className="font-semibold">{resourceName}</span>
  );

  return (
    <>
      <div className="pb-2 text-lg">Sharing</div>
      <div className="pb-2">
        A user can access a particular {boldResourceName} if:
      </div>
      <div className="pl-4 mb-4">
        {
          <div className="space-y-2">
            <div>
              <label className="flex justify-between items-start cursor-pointer space-x-4 max-w-2xl">
                <div
                  className={
                    resource.sharing.canShareWithOtherUsers
                      ? ""
                      : "text-muted-foreground"
                  }
                >
                  it is shared directly with them
                </div>
                <input
                  className="mt-1"
                  type="checkbox"
                  checked={!!resource.sharing.canShareWithOtherUsers}
                  onChange={() => {
                    setState(toggleCanShareWithOtherUsers(state, resourceName));
                    track(
                      "Modeler - Resource - Configure Access - Toggle Sharing - Can Share With Other Users",
                      {
                        resourceName,
                        canShareWithOtherUsers:
                          !resource.sharing.canShareWithOtherUsers,
                      }
                    );
                  }}
                />
              </label>
              {resource.sharing.canShareWithOtherUsers && (
                <>
                  <PrimitiveHint>
                    Grant the user a <RolePrimitive up /> on the resource to
                    indicate that it is shared directly with them.
                  </PrimitiveHint>
                  <div className="border-l pl-4">
                    Users can share a {boldResourceName} with others as
                    following roles:
                    {resource.roles.map((role) => {
                      const canShareThisRole =
                        resource.sharing.canShareWithOtherUsers?.includes(role);
                      return (
                        <label
                          key={role}
                          className={`block cursor-pointer ${
                            canShareThisRole ? "" : "text-muted-foreground"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={canShareThisRole}
                            className="mr-2"
                            onChange={() => {
                              setState(
                                toggleCanShareWithOtherUsersRole(
                                  state,
                                  resourceName,
                                  role
                                )
                              );
                              track(
                                "Modeler - Resource - Configure Access - Toggle Sharing - Can Share With Other Users - Role",
                                {
                                  resourceName,
                                  role,
                                  canShare: !canShareThisRole,
                                }
                              );
                            }}
                          />
                          {role}
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            {resource.relations.map((relation) => {
              const canShareWithThisParent =
                resource.sharing.canShareWithParent.find(
                  (sharing) => sharing.parent === relation.resource
                );
              const boldParentName = (
                <span className="font-semibold">{relation.resource}</span>
              );
              const parentResource = state.resources.find(
                (r) => r.name === relation.resource
              );
              if (parentResource === undefined) return null;
              return (
                <div key={relation.resource}>
                  <label className="flex justify-between items-start cursor-pointer space-x-4 max-w-2xl">
                    <div
                      className={
                        canShareWithThisParent ? "" : "text-muted-foreground"
                      }
                    >
                      it is shared with an associated {boldParentName} they have
                      access to
                    </div>
                    <input
                      className="mt-1"
                      type="checkbox"
                      checked={!!canShareWithThisParent}
                      onChange={() => {
                        setState(
                          toggleCanShareWithParent(
                            state,
                            resourceName,
                            relation.resource
                          )
                        );
                        track(
                          "Modeler - Resource - Configure Access - Toggle Sharing - Can Share With Parent",
                          {
                            resourceName,
                            canShareWithParent:
                              !resource.sharing.canShareWithParent,
                          }
                        );
                      }}
                    />
                  </label>
                  {canShareWithThisParent && (
                    <>
                      <PrimitiveHint>
                        If a user has a <RolePrimitive up /> on a particular{" "}
                        {relation.resource} that has a <RelationPrimitive up />{" "}
                        with a particular {resourceName}, setting an{" "}
                        <AttributePrimitive up /> on the {resourceName} gives
                        them access to it.
                      </PrimitiveHint>
                      <div className="border-l pl-4">
                        When a {boldResourceName} is shared with its parent{" "}
                        {boldParentName}, users with a role on the parent
                        receive a role on the {boldResourceName} according to
                        this table:
                        <table className="table-auto ml-8">
                          <thead>
                            <tr>
                              <th className="border-r px-4 py-2">
                                Role on {relation.resource}
                              </th>
                              <th className="px-4 py-2">
                                Role on {resourceName}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {parentResource.roles.map((parentResourceRole) => (
                              <tr key={parentResourceRole} className="border-t">
                                <td className="border-r text-right px-4 py-2">
                                  {parentResourceRole}
                                </td>
                                <td className="px-4 py-2">
                                  <select
                                    value={
                                      canShareWithThisParent.configuration.find(
                                        ({ parentRole }) =>
                                          parentRole === parentResourceRole
                                      )?.thisRole ?? ""
                                    }
                                    onChange={(e) => {
                                      setState(
                                        updateCanShareWithParentInheritance(
                                          state,
                                          resourceName,
                                          relation.resource,
                                          parentResourceRole,
                                          e.target.value
                                        )
                                      );
                                      track(
                                        "Modeler - Resource - Configure Access - Toggle Sharing - Can Share With Parent - Role Inheritance",
                                        {
                                          parentResource: relation.resource,
                                          parentResourceRole,
                                          childResource: resourceName,
                                          childRole: e.target.value,
                                        }
                                      );
                                    }}
                                    className="py-0 px-2 w-28"
                                  >
                                    <option value="">(none)</option>
                                    {resource.roles.map((role) => (
                                      <option key={role} value={role}>
                                        {role}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
            <div>
              <label className="flex justify-between items-start cursor-pointer space-x-4 max-w-2xl">
                <div
                  className={
                    resource.sharing.canSharePublic
                      ? ""
                      : "text-muted-foreground"
                  }
                >
                  it is shared publicly
                </div>
                <input
                  className="mt-1"
                  type="checkbox"
                  checked={!!resource.sharing.canSharePublic}
                  onChange={() => {
                    setState(toggleCanSharePublic(state, resourceName));
                    track(
                      "Modeler - Resource - Configure Access - Toggle Sharing - Can Share Public",
                      {
                        resourceName,
                        canSharePublic: !resource.sharing.canSharePublic,
                      }
                    );
                  }}
                />
              </label>
              {resource.sharing.canSharePublic && (
                <>
                  <PrimitiveHint>
                    Set an <AttributePrimitive up /> on the resource to indicate
                    that it is shared publicly.
                  </PrimitiveHint>
                  <div className="border-l pl-4">
                    Users can perform the following actions on public resources:
                    {resource.permissions.map((permission) => {
                      const thisPermissionIsPublic =
                        resource.sharing.canSharePublic?.includes(permission);
                      return (
                        <label
                          key={permission}
                          className={`block cursor-pointer ${
                            thisPermissionIsPublic
                              ? ""
                              : "text-muted-foreground"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={thisPermissionIsPublic}
                            className="mr-2"
                            onChange={() => {
                              setState(
                                toggleCanSharePublicPermission(
                                  state,
                                  resourceName,
                                  permission
                                )
                              );
                              track(
                                "Modeler - Resource - Configure Access - Toggle Sharing - Can Share Public - Permission",
                                {
                                  resourceName,
                                  permission,
                                  isPublic: !thisPermissionIsPublic,
                                }
                              );
                            }}
                          />
                          {permission}
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        }
      </div>
    </>
  );
};

export const ResourceRolesEditor: React.FC<{
  state: State;
  setState: (state: State) => void;
  resourceName: string;
  track: (event: string, properties: { [key: string]: unknown }) => void;
}> = ({ state, setState, resourceName, track }) => {
  const resource = state.resources.find((r) => r.name === resourceName);
  if (!resource) return <div>oopsie!</div>;
  const grantedAncestors = grantedParents(state, resourceName);
  const boldResourceName = (
    <span className="font-semibold">{resourceName}</span>
  );

  // Restriction: A resource with no relations must have its own roles.
  const topLevel = !resource.relations.some(
    (r) => r.resource !== resource.name
  );

  return (
    <div className="pl-4 pb-4">
      <div className="pb-4">
        <label className="flex justify-between items-start cursor-pointer space-x-4 max-w-2xl">
          <div
            className={
              resource.isGrantedAccessTo ? "" : "text-muted-foreground"
            }
          >
            You can grant users direct access to a particular {boldResourceName}
            .
            {resource.isGrantedAccessTo && (
              <PrimitiveHint>
                A user can be assigned a <RolePrimitive up /> on the resource to
                grant access.
              </PrimitiveHint>
            )}
          </div>
          {topLevel ? null : (
            <input
              className="mt-1"
              type="checkbox"
              checked={resource.isGrantedAccessTo}
              onChange={() => {
                const isGrantedAccessTo = !resource.isGrantedAccessTo;
                setState(
                  updateResourceIsGrantedAccessTo(
                    state,
                    resourceName,
                    isGrantedAccessTo
                  )
                );
                track(
                  "Modeler - Resource - Configure Access - isGrantedAccessTo",
                  {
                    resourceName,
                    isGrantedAccessTo,
                  }
                );
              }}
            />
          )}
        </label>
      </div>
      {resource.isGrantedAccessTo && (
        <div className="pb-4">
          Roles:
          <ul className="list-disc pl-4">
            {resource.roles.map((role) => (
              <li key={role} className="my-1">
                <EditableItem
                  value={role}
                  setValue={(newRole) => {
                    setState(editRole(state, resource.name, role, newRole));
                    track("Modeler - Resource - Configure Access - Edit Role", {
                      resourceName,
                      role,
                      newRole,
                    });
                  }}
                  deleteValue={() => {
                    if (resource.roles.length === 1) {
                      alert("Must have at least one role.");
                      return;
                    }
                    setState(deleteRole(state, resource.name, role));
                    track(
                      "Modeler - Resource - Configure Access - Delete Role",
                      {
                        resourceName,
                        role,
                      }
                    );
                  }}
                />
              </li>
            ))}
            <li>
              <AddItem
                placeholder="Add role"
                addItem={(role) => {
                  setState(addRole(state, resource.name, role));
                  track("Modeler - Resource - Configure Access - Add Role", {
                    resourceName,
                    role,
                  });
                }}
              />
            </li>
          </ul>
        </div>
      )}
      {grantedAncestors.map(
        ({ ancestorResource, ancestorRoles, directRelation }) => {
          const boldAncestor = (
            <span className="font-semibold">{ancestorResource}</span>
          );
          const primitiveHint = (
            <PrimitiveHint>
              Grant the user a <RolePrimitive /> on a particular{" "}
              {ancestorResource} and use{" "}
              {directRelation.resource === ancestorResource ? (
                <>
                  a <RelationPrimitive />
                </>
              ) : (
                <>
                  <RelationPrimitive />s
                </>
              )}{" "}
              to associate it with a particular {resourceName}.
            </PrimitiveHint>
          );
          switch (directRelation.roleInheritance.kind) {
            case "simple":
              return (
                <Fragment key={`simple-${resourceName}-${ancestorResource}`}>
                  <div>
                    A user has access to a particular {boldResourceName} if they
                    have a <RolePrimitive /> on an associated {boldAncestor}.
                  </div>
                  {primitiveHint}
                </Fragment>
              );
            case "configured":
              const { configuration } = directRelation.roleInheritance;
              return (
                <Fragment
                  key={`configured-${resourceName}-${ancestorResource}`}
                >
                  <div className="max-w-2xl">
                    A user also has access to a particular {boldResourceName} if
                    they have access to an associated {boldAncestor}. This table
                    defines how a user{"'"}s <RolePrimitive /> on an associated{" "}
                    {boldAncestor} determines their <RolePrimitive /> on a
                    particular {boldResourceName}:
                  </div>
                  <table className="table-auto ml-8">
                    <thead>
                      <tr>
                        <th className="border-r px-4 py-2">
                          Role on {ancestorResource}
                        </th>
                        <th className="px-4 py-2">Role on {resourceName}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ancestorRoles.map((ancestorRole) => (
                        <tr key={ancestorRole} className="border-t">
                          <td className="border-r text-right px-4 py-2">
                            {ancestorRole}
                          </td>
                          <td className="px-4 py-2">
                            <select
                              value={
                                configuration.find(
                                  ({ parentRole }) =>
                                    parentRole === ancestorRole
                                )?.thisRole ?? ""
                              }
                              onChange={(e) => {
                                setState(
                                  updateRoleInheritanceConfiguration(
                                    state,
                                    resourceName,
                                    directRelation.resource,
                                    ancestorRole,
                                    e.target.value
                                  )
                                );
                                track(
                                  "Modeler - Resource - Configure Access - Role Inheritance",
                                  {
                                    ancestorResource,
                                    ancestorRole,
                                    childResource: resourceName,
                                    childRole: e.target.value,
                                  }
                                );
                              }}
                              className="py-0 px-2 w-28"
                            >
                              <option value="">(none)</option>
                              {resource.roles.map((role) => (
                                <option key={role} value={role}>
                                  {role}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {primitiveHint}
                </Fragment>
              );
          }
        }
      )}
    </div>
  );
};

const ResourceAccessEditor: React.FC<{
  state: State;
  setState: (state: State) => void;
  resourceName: string;
  track: (event: string, properties: { [key: string]: unknown }) => void;
}> = ({ state, setState, resourceName, track }) => {
  const resource = state.resources.find((r) => r.name === resourceName);
  if (!resource) return <div>oopsie!</div>;
  return (
    <>
      <div className="text-lg pb-2">Roles</div>
      <ResourceRolesEditor
        state={state}
        setState={setState}
        resourceName={resourceName}
        track={track}
      />
      <SharingEditor
        state={state}
        setState={setState}
        resourceName={resourceName}
        track={track}
      />
    </>
  );
};

export const ResourcePermissionsEditor: React.FC<{
  state: State;
  setState: (state: State) => void;
  resourceName: string;
  track: (event: string, properties: { [key: string]: unknown }) => void;
}> = ({ state, setState, resourceName, track }) => {
  const resource = state.resources.find((r) => r.name === resourceName);
  if (!resource) return <div>oopsie!</div>;
  const grantedAncestors = grantedParents(state, resourceName);
  const boldResourceName = (
    <span className="font-semibold">{resourceName}</span>
  );
  return (
    <>
      {resource.isGrantedAccessTo ? (
        <>
          <div>
            This table defines how a user{"'"}s <RolePrimitive /> on a
            particular {boldResourceName} determines what they may do to it:
          </div>
          <table className="pb-4 table-auto mx-auto max-w-2xl">
            <thead>
              <tr>
                <th>Permission</th>
                {resource.roles.map((role) => (
                  <th key={role} className="border-l w-24">
                    {role}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {resource.permissions.map((permission) => (
                <tr key={permission} className="border-t">
                  <td>
                    <EditableItem
                      value={permission}
                      setValue={(newPermission) => {
                        setState(
                          editPermission(
                            state,
                            resource.name,
                            permission,
                            newPermission
                          )
                        );
                        track(
                          "Modeler - Resource - Configure Permissions - Edit Permission",
                          {
                            resourceName,
                            permission,
                            newPermission,
                          }
                        );
                      }}
                      deleteValue={() => {
                        if (resource.permissions.length === 1) {
                          alert("Must have at least one permission.");
                          return;
                        }
                        setState(
                          deletePermission(state, resource.name, permission)
                        );
                        track(
                          "Modeler - Resource - Configure Permissions - Delete Permission",
                          {
                            resourceName,
                            permission,
                          }
                        );
                      }}
                    />
                  </td>
                  {resource.roles.map((role) => (
                    <td key={role} className="border-l text-center">
                      <input
                        type="checkbox"
                        onChange={() => {
                          setState(
                            toggleRolePermissionImplication(
                              state,
                              resource.name,
                              role,
                              permission
                            )
                          );
                          track(
                            "Modeler - Resource - Configure Permissions - Toggle Role Permission Implication",
                            {
                              resourceName,
                              role,
                              permission,
                            }
                          );
                        }}
                        checked={resource.rolePermissionImplications.some(
                          (implication) =>
                            implication.role === role &&
                            implication.permission === permission
                        )}
                      />
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <td>
                  <AddItem
                    placeholder="Add permission"
                    addItem={(permission) => {
                      setState(addPermission(state, resource.name, permission));
                      track(
                        "Modeler - Resource - Configure Permissions - Add Permission",
                        {
                          resourceName,
                          permission,
                        }
                      );
                    }}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </>
      ) : (
        grantedAncestors.map(({ ancestorResource, ancestorRoles }) => {
          const boldAncestor = (
            <span className="font-semibold">{ancestorResource}</span>
          );
          return (
            <Fragment key={ancestorResource}>
              <div className="my-4 max-w-2xl">
                This table defines how a user{"'"}s <RolePrimitive /> on an
                associated {boldAncestor} determines what they may do to a
                particular {boldResourceName}:
              </div>
              <table className="pb-4 table-auto ml-8 mb-8">
                <thead>
                  <tr>
                    <th>Permission</th>
                    {ancestorRoles.map((role) => (
                      <th key={role} className="border-l w-24">
                        {role} on {ancestorResource}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {resource.permissions.map((permission) => (
                    <tr key={permission} className="border-t">
                      <td>
                        <EditableItem
                          value={permission}
                          setValue={(newPermission) => {
                            setState(
                              editPermission(
                                state,
                                resource.name,
                                permission,
                                newPermission
                              )
                            );
                            track(
                              "Modeler - Resource - Configure Permissions - Edit Permission",
                              {
                                resourceName,
                                permission,
                                newPermission,
                                ancestorResource,
                              }
                            );
                          }}
                          deleteValue={() => {
                            if (resource.permissions.length === 1) {
                              alert("Must have at least one permission.");
                              return;
                            }
                            setState(
                              deletePermission(state, resource.name, permission)
                            );
                            track(
                              "Modeler - Resource - Configure Permissions - Delete Permission",
                              {
                                resourceName,
                                permission,
                                ancestorResource,
                              }
                            );
                          }}
                        />
                      </td>
                      {ancestorRoles.map((role) => (
                        <td key={role} className="border-l text-center">
                          <input
                            type="checkbox"
                            onChange={() => {
                              setState(
                                toggleRolePermissionImplication(
                                  state,
                                  resource.name,
                                  role,
                                  permission
                                )
                              );
                              track(
                                "Modeler - Resource - Configure Permissions - Toggle Role Permission Implication",
                                {
                                  resourceName,
                                  role,
                                  permission,
                                  ancestorResource,
                                }
                              );
                            }}
                            checked={resource.rolePermissionImplications.some(
                              (implication) =>
                                implication.role === role &&
                                implication.permission === permission
                            )}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr>
                    <td>
                      <AddItem
                        placeholder="Add permission"
                        addItem={(permission) => {
                          setState(
                            addPermission(state, resource.name, permission)
                          );
                          track(
                            "Modeler - Resource - Configure Permissions - Add Permission",
                            {
                              resourceName,
                              permission,
                              ancestorResource,
                            }
                          );
                        }}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </Fragment>
          );
        })
      )}
    </>
  );
};

(ModelerPage as { isPublic?: boolean }).isPublic = true; // skip some requests in _app.tsx
export default ModelerPage;
